import type { SVGProps } from "react";

/**
 * Fundred — "Spark" icon system.
 *
 * Signature language (brand DNA):
 * - Solid filled geometry, no strokes. 24×24 grid, 2u corner radius.
 * - Two tonal planes: base (currentColor) + soft plane (opacity .22).
 * - Every icon carries a single "spark" — a small accent dot — placed with
 *   intent. The spark is the brand signature; it is never decorative.
 * - Distinct silhouettes: each icon reads at 16px from its outline alone.
 *
 * Props:
 *   size   — pixel size (default 24)
 *   accent — spark color (default #F5B544 — Fundred gold)
 */

export type TriIconProps = Omit<SVGProps<SVGSVGElement>, "color"> & {
  size?: number | string;
  accent?: string;
};

const svgProps = (p: TriIconProps) => ({
  width: p.size ?? 24,
  height: p.size ?? 24,
  viewBox: "0 0 24 24",
  fill: "currentColor",
  xmlns: "http://www.w3.org/2000/svg",
  ...p,
});

const SPARK = "#F5B544";

/* ─────────────── Brand mark ─────────────── */
/* Fundred — three converging sphere-petals forming one unified system.
   Each petal is a lens (vesica piscis) radiating from center at 120° intervals.
   The gold convergence point is where three become one — the intelligence core. */
export const LogoMark = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    {/* Soft echo — all three petals at low opacity for depth */}
    <path d="M12 12 A6 6 0 0 1 12 3 A6 6 0 0 1 12 12 Z" opacity={0.18} />
    <path d="M12 12 A6 6 0 0 1 4.2 16.5 A6 6 0 0 1 12 12 Z" opacity={0.18} />
    <path d="M12 12 A6 6 0 0 1 19.8 16.5 A6 6 0 0 1 12 12 Z" opacity={0.18} />
    {/* Petal 1 — top (full tone, the lead plane) */}
    <path d="M12 12 A6 6 0 0 1 12 3 A6 6 0 0 1 12 12 Z" />
    {/* Petal 2 — lower-left (mid tone) */}
    <path d="M12 12 A6 6 0 0 1 4.2 16.5 A6 6 0 0 1 12 12 Z" opacity={0.58} />
    {/* Petal 3 — lower-right (soft tone) */}
    <path d="M12 12 A6 6 0 0 1 19.8 16.5 A6 6 0 0 1 12 12 Z" opacity={0.35} />
    {/* Convergence core — where three spheres unify */}
    <circle cx={12} cy={12} r={1.8} fill={accent} />
  </svg>
);

/* ─────────────── Category ─────────────── */
// Students — a scholar disc: half-circle head over a chevron body, spark = tassel
export const StudentsIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M4 10 12 5.5 20 10l-8 4.5L4 10Z" opacity={0.22} />
    <path d="M4 10 12 5.5 20 10l-8 4.5L4 10Z" />
    <path d="M7.5 12.4v2.4c0 1.4 2 2.5 4.5 2.5s4.5-1.1 4.5-2.5v-2.4L12 15 7.5 12.4Z" />
    <rect x={19.1} y={10.4} width={1.4} height={5.2} rx={0.7} />
    <circle cx={19.8} cy={17.4} r={1.4} fill={accent} />
  </svg>
);

// Parents — two overlapping domes (one taller), spark bridges them = connection
export const ParentsIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M4 20c0-3.6 2.5-6 5.5-6S15 16.4 15 20H4Z" opacity={0.22} />
    <circle cx={9.5} cy={9} r={3.2} />
    <path d="M4 20c0-3.6 2.5-6 5.5-6S15 16.4 15 20H4Z" />
    <circle cx={16.5} cy={10.5} r={2.5} opacity={0.55} />
    <path
      d="M14 20c.2-2.6 1.6-4.6 3.7-5.2 2.4-.6 4.3 1 4.3 3.6V20h-8Z"
      opacity={0.55}
    />
    <circle cx={13} cy={13} r={1.4} fill={accent} />
  </svg>
);

// Schools — asymmetric gabled tower with a bright window
export const SchoolsIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M4 20V11l4-3v12H4Z" opacity={0.22} />
    <path d="M4 20V11l4-3v12H4Z" />
    <path d="M8 20V6.5L14 3l6 4v13H8Z" />
    <path d="M8 6.5 14 3l6 4-6 3-6-3.5Z" opacity={0.35} />
    <rect x={12} y={13} width={4} height={7} rx={0.8} fill="#fff" opacity={0.9} />
    <rect x={13.2} y={14.2} width={1.6} height={2.4} rx={0.4} fill={accent} />
  </svg>
);

// Teachers — an open ledger with a diagonal spine, spark = idea point
export const TeachersIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M3 6.5c3-1 6-1 9 .8V20c-3-1.8-6-1.8-9-.8V6.5Z" opacity={0.22} />
    <path d="M3 6.5c3-1 6-1 9 .8V20c-3-1.8-6-1.8-9-.8V6.5Z" />
    <path d="M21 6.5c-3-1-6-1-9 .8V20c3-1.8 6-1.8 9-.8V6.5Z" />
    <path d="M21 6.5c-3-1-6-1-9 .8V20c3-1.8 6-1.8 9-.8V6.5Z" opacity={0.35} />
    <circle cx={12} cy={4.5} r={1.6} fill={accent} />
  </svg>
);

// Universities — twin columns with a keystone; spark = crown
export const UniversitiesIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <rect x={4} y={9} width={5} height={11} rx={1.2} opacity={0.22} />
    <rect x={4} y={9} width={5} height={11} rx={1.2} />
    <rect x={11} y={7} width={6} height={13} rx={1.2} />
    <rect x={11} y={7} width={6} height={13} rx={1.2} opacity={0.35} />
    <rect x={5.5} y={11.5} width={2} height={2} rx={0.4} fill="#fff" opacity={0.9} />
    <rect x={5.5} y={15} width={2} height={2} rx={0.4} fill="#fff" opacity={0.9} />
    <rect x={12.5} y={10.5} width={3} height={2.4} rx={0.4} fill="#fff" opacity={0.9} />
    <rect x={12.5} y={14.5} width={3} height={2.4} rx={0.4} fill="#fff" opacity={0.9} />
    <path d="M11 7 14 3l3 4h-6Z" fill={accent} />
  </svg>
);

// Businesses — stacked awning + storefront base; spark = doorbell / signal
export const BusinessesIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M4 11h16v9H4z" opacity={0.22} />
    <path d="M4 11h16v9H4z" />
    <path d="M3 7.5 5 5h14l2 2.5v2.5c-1.6 0-2.7-.9-3-2-.3 1.1-1.4 2-3 2s-2.7-.9-3-2c-.3 1.1-1.4 2-3 2s-2.7-.9-3-2c-.3 1.1-1.4 2-3 2V7.5Z" />
    <rect x={9.5} y={13.5} width={5} height={6.5} rx={0.6} fill="#fff" opacity={0.9} />
    <circle cx={18} cy={16} r={1.4} fill={accent} />
  </svg>
);

/* ─────────────── Navigation ─────────────── */
// Home — asymmetric roofline, spark = hearth window
export const HomeIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M3.5 11 12 4.2l8.5 6.8v9a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1v-9Z" opacity={0.22} />
    <path d="M3.5 11 12 4.2l8.5 6.8H3.5Z" />
    <path d="M5 11h14v9.5a.5.5 0 0 1-.5.5h-13a.5.5 0 0 1-.5-.5V11Z" opacity={0.55} />
    <rect x={10.4} y={14} width={3.2} height={7} rx={0.6} fill="#fff" opacity={0.95} />
    <circle cx={12} cy={16.5} r={0.9} fill={accent} />
  </svg>
);

// Family — three chevrons of decreasing size, spark = focal child
export const FamilyIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M3 20c.6-3.4 2.7-5.6 5.5-5.6S13.4 16.6 14 20H3Z" opacity={0.22} />
    <circle cx={8.5} cy={8.5} r={3} />
    <path d="M3 20c.6-3.4 2.7-5.6 5.5-5.6S13.4 16.6 14 20H3Z" />
    <circle cx={16} cy={10} r={2.2} opacity={0.55} />
    <path d="M13.5 20c.5-2.5 1.9-4.2 3.8-4.5 2.1-.3 3.7 1.2 3.7 3.4V20h-7.5Z" opacity={0.55} />
    <circle cx={19.5} cy={7} r={1.4} fill={accent} />
  </svg>
);

// Finance — layered coin/bill hybrid with a chevron up
export const FinanceIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <rect x={3} y={7} width={18} height={12} rx={2.4} opacity={0.22} />
    <rect x={3} y={7} width={18} height={12} rx={2.4} />
    <circle cx={12} cy={13} r={3.4} fill="#fff" opacity={0.9} />
    <path d="M12 10.5v5M10.2 12h3.6" stroke="currentColor" strokeWidth={1.4} strokeLinecap="round" fill="none" />
    <path d="M3 7l3-3h12l3 3H3Z" opacity={0.55} />
    <circle cx={17.5} cy={13} r={1.4} fill={accent} />
  </svg>
);

// Messages — offset speech planes, spark = active thread
export const MessagesIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M3.5 8a3 3 0 0 1 3-3H15a3 3 0 0 1 3 3v5a3 3 0 0 1-3 3h-4l-4 3.4V16H6.5a3 3 0 0 1-3-3V8Z" opacity={0.22} />
    <path d="M3.5 8a3 3 0 0 1 3-3H15a3 3 0 0 1 3 3v5a3 3 0 0 1-3 3h-4l-4 3.4V16H6.5a3 3 0 0 1-3-3V8Z" />
    <path d="M9 11a3 3 0 0 1 3-3h5.5a3 3 0 0 1 3 3v3.6a3 3 0 0 1-3 3H17l-2 2v-2h-3a3 3 0 0 1-3-3V11Z" opacity={0.55} />
    <circle cx={20} cy={7} r={1.7} fill={accent} />
  </svg>
);

// Calendar — tabbed header, chunky date tile, spark = today
export const CalendarIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <rect x={3} y={5.5} width={18} height={15} rx={2.4} opacity={0.22} />
    <rect x={3} y={5.5} width={18} height={15} rx={2.4} />
    <rect x={3} y={5.5} width={18} height={5} rx={2.4} opacity={0.55} />
    <rect x={7} y={3} width={2} height={4.5} rx={0.8} />
    <rect x={15} y={3} width={2} height={4.5} rx={0.8} />
    <rect x={7} y={13} width={4.5} height={4.5} rx={0.8} fill="#fff" opacity={0.95} />
    <circle cx={16.5} cy={15.2} r={1.6} fill={accent} />
  </svg>
);

// Campus Exchange — two orbiting arcs, spark = exchange node
export const CampusExchangeIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <circle cx={12} cy={12} r={9} opacity={0.22} />
    <path d="M12 3a9 9 0 0 1 8.5 6H15a3 3 0 0 0-3 3v-9Z" />
    <path d="M12 21a9 9 0 0 1-8.5-6H9a3 3 0 0 1 3-3v9Z" opacity={0.55} />
    <circle cx={12} cy={12} r={2.2} fill="#fff" opacity={0.95} />
    <circle cx={12} cy={12} r={1.2} fill={accent} />
  </svg>
);

// Documents — folded corner, dogeared plane, spark = seal
export const DocumentsIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M5 4.5a1.5 1.5 0 0 1 1.5-1.5H14l5 5v11.5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 19.5v-15Z" opacity={0.22} />
    <path d="M5 4.5a1.5 1.5 0 0 1 1.5-1.5H14l5 5v11.5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 19.5v-15Z" />
    <path d="M14 3v3.5A1.5 1.5 0 0 0 15.5 8H19L14 3Z" opacity={0.55} />
    <rect x={7.5} y={12} width={9} height={1.4} rx={0.7} fill="#fff" opacity={0.9} />
    <rect x={7.5} y={15} width={6} height={1.4} rx={0.7} fill="#fff" opacity={0.9} />
    <circle cx={16} cy={16} r={1.6} fill={accent} />
  </svg>
);

// Consent — shield with keyhole check, spark = seal of approval
export const ConsentIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M12 2.6 4 5.4v6.8c0 4.6 3.3 8.4 8 9.2 4.7-.8 8-4.6 8-9.2V5.4L12 2.6Z" opacity={0.22} />
    <path d="M12 2.6 4 5.4v6.8c0 4.6 3.3 8.4 8 9.2V2.6Z" />
    <path d="M12 2.6v18.8c4.7-.8 8-4.6 8-9.2V5.4L12 2.6Z" opacity={0.55} />
    <path
      d="m8.2 12.2 2.6 2.6 4.6-5"
      fill="none"
      stroke="#fff"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity={0.95}
    />
    <circle cx={17.5} cy={6.5} r={1.4} fill={accent} />
  </svg>
);

/* ─────────────── Utility ─────────────── */
// Bell — asymmetric campanile bell, spark = notification pulse
export const BellIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M5.5 17V11a6.5 6.5 0 0 1 13 0v6l1.4 2H4.1L5.5 17Z" opacity={0.22} />
    <path d="M5.5 17V11a6.5 6.5 0 0 1 13 0v6l1.4 2H4.1L5.5 17Z" />
    <path d="M5.5 17V11a6.5 6.5 0 0 1 6.5-6.5V19H4.1L5.5 17Z" opacity={0.55} />
    <path d="M10 20.2a2 2 0 0 0 4 0h-4Z" />
    <circle cx={18} cy={6} r={2} fill={accent} />
  </svg>
);

// Search — offset lens with a bold handle, spark = pinpoint
export const SearchIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <circle cx={11} cy={11} r={6.5} opacity={0.22} />
    <circle cx={11} cy={11} r={6.5} />
    <circle cx={11} cy={11} r={3.4} fill="#fff" opacity={0.9} />
    <rect
      x={16.4}
      y={14.6}
      width={2.4}
      height={7}
      rx={1.2}
      transform="rotate(-45 16.4 14.6)"
    />
    <circle cx={11} cy={11} r={1.2} fill={accent} />
  </svg>
);

/* ─────────────── Child-Tab Icons ─────────────── */
// Overview — asymmetric bento grid, spark = active tile
export const OverviewIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <rect x={3} y={3} width={8.5} height={8.5} rx={2} opacity={0.22} />
    <rect x={3} y={3} width={8.5} height={8.5} rx={2} />
    <rect x={13} y={3} width={8} height={5.5} rx={2} opacity={0.55} />
    <rect x={13} y={10.5} width={8} height={10.5} rx={2} />
    <rect x={3} y={13} width={8.5} height={8} rx={2} opacity={0.55} />
    <rect x={4.5} y={4.5} width={5.5} height={5.5} rx={1.2} fill="#fff" opacity={0.9} />
    <rect x={14.5} y={12.5} width={5} height={6.5} rx={1.2} fill="#fff" opacity={0.9} />
    <circle cx={17} cy={15.8} r={1.2} fill={accent} />
  </svg>
);

// Academics — graduation cap with tassel, spark = tassel bob
export const AcademicsIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M2 9.5 12 4.5l10 5-10 5L2 9.5Z" opacity={0.22} />
    <path d="M2 9.5 12 4.5l10 5-10 5L2 9.5Z" />
    <path d="M6 11.5V17c0 1.4 2.7 2.5 6 2.5s6-1.1 6-2.5v-5.5L12 14 6 11.5Z" opacity={0.55} />
    <rect x={18.5} y={9.5} width={1.6} height={5.5} rx={0.8} />
    <circle cx={19.3} cy={16.5} r={1.4} fill={accent} />
  </svg>
);

// Attendance — clock face, spark = current minute
export const AttendanceIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <circle cx={12} cy={12} r={9} opacity={0.22} />
    <circle cx={12} cy={12} r={9} />
    <circle cx={12} cy={12} r={6} fill="#fff" opacity={0.85} />
    <path d="M12 7.5v4.8l3 1.8" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" fill="none" />
    <circle cx={12} cy={12} r={1.2} fill={accent} />
  </svg>
);

// Wallet — card with chip slot, spark = contactless dot
export const WalletIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <rect x={3} y={5.5} width={18} height={13} rx={2.8} opacity={0.22} />
    <rect x={3} y={5.5} width={18} height={13} rx={2.8} />
    <rect x={3} y={5.5} width={18} height={4} rx={2.8} opacity={0.55} />
    <rect x={13.5} y={11.5} width={6} height={4.5} rx={1.4} fill="#fff" opacity={0.9} />
    <circle cx={16.5} cy={13.8} r={1} fill={accent} />
  </svg>
);

// Health — heart with cross, spark = pulse
export const HealthIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M12 21C7.5 17.5 3.5 14 3.5 9.5 3.5 6.5 5.5 4.5 8.5 4.5c1.8 0 2.8 1 3.5 2.5.7-1.5 1.7-2.5 3.5-2.5 3 0 5 2 5 5 0 4.5-4 8-8.5 11.5Z" opacity={0.22} />
    <path d="M12 21C7.5 17.5 3.5 14 3.5 9.5 3.5 6.5 5.5 4.5 8.5 4.5c1.8 0 2.8 1 3.5 2.5.7-1.5 1.7-2.5 3.5-2.5 3 0 5 2 5 5 0 4.5-4 8-8.5 11.5Z" />
    <rect x={10.4} y={9.5} width={3.2} height={5} rx={0.6} fill="#fff" opacity={0.9} />
    <path d="M12 10.8v2.4M10.8 12h2.4" stroke="currentColor" strokeWidth={1} strokeLinecap="round" />
    <circle cx={17.5} cy={8.5} r={1.4} fill={accent} />
  </svg>
);

// Transport — bus silhouette, spark = headlight
export const TransportIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <rect x={4} y={4.5} width={16} height={13} rx={3} opacity={0.22} />
    <rect x={4} y={4.5} width={16} height={13} rx={3} />
    <rect x={4} y={8} width={16} height={1.5} opacity={0.3} />
    <rect x={5.5} y={5.8} width={5} height={2} rx={0.6} fill="#fff" opacity={0.85} />
    <rect x={13.5} y={5.8} width={5} height={2} rx={0.6} fill="#fff" opacity={0.85} />
    <circle cx={8} cy={18.5} r={2.2} />
    <circle cx={16} cy={18.5} r={2.2} />
    <circle cx={8} cy={18.5} r={0.9} fill="#fff" opacity={0.9} />
    <circle cx={16} cy={18.5} r={0.9} fill="#fff" opacity={0.9} />
    <circle cx={18.5} cy={12} r={1.3} fill={accent} />
  </svg>
);

// Timeline — vertical spine with nodes, spark = current event
export const TimelineIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <rect x={11} y={3} width={2} height={18} rx={1} opacity={0.22} />
    <rect x={11} y={3} width={2} height={18} rx={1} />
    <circle cx={12} cy={6.5} r={2.4} fill="#fff" opacity={0.9} />
    <circle cx={12} cy={12} r={2.4} fill="#fff" opacity={0.9} />
    <circle cx={12} cy={17.5} r={2.4} fill="#fff" opacity={0.9} />
    <circle cx={12} cy={12} r={1.5} fill={accent} />
  </svg>
);

/* ─────────────── Feature Icons ─────────────── */
// Fees — bill with chevron, spark = due stamp
export const FeesIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <rect x={4} y={5} width={16} height={14} rx={2.4} opacity={0.22} />
    <rect x={4} y={5} width={16} height={14} rx={2.4} />
    <rect x={4} y={5} width={16} height={4} rx={2.4} opacity={0.55} />
    <circle cx={12} cy={13} r={3} fill="#fff" opacity={0.9} />
    <path d="M12 11v4M10.5 12h3" stroke="currentColor" strokeWidth={1.4} strokeLinecap="round" fill="none" />
    <circle cx={17} cy={15.5} r={1.4} fill={accent} />
  </svg>
);

// BusRoute — curving path with endpoint pins, spark = live bus
export const BusRouteIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M5 19c0-5 3-9 7-9s7 4 7 9" opacity={0.22} />
    <path d="M5 19c0-5 3-9 7-9s7 4 7 9" stroke="currentColor" strokeWidth={2} fill="none" strokeLinecap="round" />
    <circle cx={5} cy={19} r={2.4} />
    <circle cx={19} cy={19} r={2.4} opacity={0.55} />
    <circle cx={12} cy={10} r={2} fill="#fff" opacity={0.9} />
    <circle cx={12} cy={10} r={1.2} fill={accent} />
  </svg>
);

// Receipt — serrated-edge bill, spark = approval stamp
export const ReceiptIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M5 3h14v18l-2-1.5L15 21l-2-1.5L11 21l-2-1.5L7 21l-2-1.5V3Z" opacity={0.22} />
    <path d="M5 3h14v18l-2-1.5L15 21l-2-1.5L11 21l-2-1.5L7 21l-2-1.5V3Z" />
    <rect x={7.5} y={7} width={9} height={1.5} rx={0.75} fill="#fff" opacity={0.9} />
    <rect x={7.5} y={10} width={9} height={1.5} rx={0.75} fill="#fff" opacity={0.9} />
    <rect x={7.5} y={13} width={6} height={1.5} rx={0.75} fill="#fff" opacity={0.9} />
    <circle cx={16.5} cy={16} r={1.4} fill={accent} />
  </svg>
);

// Shield — protective badge, spark = verified seal
export const ShieldIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M12 2.5 4 5.3v7c0 4.6 3.3 8.4 8 9.2V2.5Z" opacity={0.22} />
    <path d="M12 2.5 4 5.3v7c0 4.6 3.3 8.4 8 9.2V2.5Z" />
    <path d="M12 2.5v19c4.7-.8 8-4.6 8-9.2v-7L12 2.5Z" opacity={0.55} />
    <circle cx={17.5} cy={6.5} r={1.4} fill={accent} />
  </svg>
);

// Growth — trending arrow, spark = peak point
export const GrowthIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M4 18 9 13l4 2 7-7.5" opacity={0.22} />
    <path d="M4 18 9 13l4 2 7-7.5" stroke="currentColor" strokeWidth={2.2} fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15 7.5h5v5" stroke="currentColor" strokeWidth={2.2} fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx={13} cy={15} r={1.6} fill="#fff" opacity={0.9} />
    <circle cx={13} cy={15} r={1} fill={accent} />
  </svg>
);

// Sparkle — four-point star, spark = center glow
export const SparkleIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M12 2.5 14 9l6.5 2L14 13l-2 6.5L10 13l-6.5-2L10 9l2-6.5Z" opacity={0.22} />
    <path d="M12 2.5 14 9l6.5 2L14 13l-2 6.5L10 13l-6.5-2L10 9l2-6.5Z" />
    <circle cx={12} cy={11} r={1.6} fill={accent} />
  </svg>
);

// Clock — minimal timepiece, spark = current second
export const ClockIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <circle cx={12} cy={12} r={9} opacity={0.22} />
    <circle cx={12} cy={12} r={9} />
    <circle cx={12} cy={12} r={6} fill="#fff" opacity={0.85} />
    <path d="M12 7.5v4.8l3 1.8" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" fill="none" />
    <circle cx={15.5} cy={9} r={1.3} fill={accent} />
  </svg>
);

// Location — map pin, spark = exact point
export const LocationIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M12 21C7 16 5 13 5 10c0-3.5 3-6 7-6s7 2.5 7 6c0 3-2 6-7 11Z" opacity={0.22} />
    <path d="M12 21C7 16 5 13 5 10c0-3.5 3-6 7-6s7 2.5 7 6c0 3-2 6-7 11Z" />
    <circle cx={12} cy={10} r={2.8} fill="#fff" opacity={0.9} />
    <circle cx={12} cy={10} r={1.4} fill={accent} />
  </svg>
);

// Book — open pages, spark = bookmark
export const BookIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M4 4.5c3-1 6-1 8 .8V20c-2-1.8-5-1.8-8-.8V4.5Z" opacity={0.22} />
    <path d="M4 4.5c3-1 6-1 8 .8V20c-2-1.8-5-1.8-8-.8V4.5Z" />
    <path d="M20 4.5c-3-1-6-1-8 .8V20c2-1.8 5-1.8 8-.8V4.5Z" opacity={0.55} />
    <rect x={11} y={4.5} width={2} height={5} rx={0.6} fill={accent} />
  </svg>
);

// Device — smartphone, spark = notification badge
export const DeviceIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <rect x={6} y={2.5} width={12} height={19} rx={3} opacity={0.22} />
    <rect x={6} y={2.5} width={12} height={19} rx={3} />
    <rect x={8.5} y={5} width={7} height={11} rx={1.2} fill="#fff" opacity={0.88} />
    <circle cx={12} cy={18.5} r={1.2} fill="#fff" opacity={0.9} />
    <circle cx={17} cy={5.5} r={1.6} fill={accent} />
  </svg>
);

// Building — institutional tower, spark = lit window
export const Building2Icon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <rect x={4} y={3} width={7} height={18} rx={1.4} opacity={0.22} />
    <rect x={4} y={3} width={7} height={18} rx={1.4} />
    <rect x={13} y={7} width={7} height={14} rx={1.4} opacity={0.55} />
    <rect x={5.5} y={5.5} width={4} height={2} rx={0.4} fill="#fff" opacity={0.9} />
    <rect x={5.5} y={9} width={4} height={2} rx={0.4} fill="#fff" opacity={0.9} />
    <rect x={5.5} y={12.5} width={4} height={2} rx={0.4} fill="#fff" opacity={0.9} />
    <rect x={14.5} y={9.5} width={4} height={2} rx={0.4} fill="#fff" opacity={0.85} />
    <rect x={14.5} y={13} width={4} height={2} rx={0.4} fill="#fff" opacity={0.85} />
    <circle cx={16.5} cy={5} r={1.4} fill={accent} />
  </svg>
);

/* ─────────────── Utility Icons ─────────────── */
// Plus — circular badge with cross, spark = focal center
export const PlusIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <circle cx={12} cy={12} r={9} opacity={0.22} />
    <circle cx={12} cy={12} r={9} />
    <path d="M12 7v10M7 12h10" stroke="#fff" strokeWidth={2} strokeLinecap="round" />
    <circle cx={12} cy={12} r={1.2} fill={accent} />
  </svg>
);

// Check — circular badge with checkmark, spark = confirmation
export const CheckIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <circle cx={12} cy={12} r={9} opacity={0.22} />
    <circle cx={12} cy={12} r={9} />
    <path d="m8 12 3 3 5-5.5" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <circle cx={16} cy={8} r={1.4} fill={accent} />
  </svg>
);

// ArrowRight — directional chevron, spark = destination
export const ArrowRightIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <rect x={3} y={10.5} width={14} height={3} rx={1.5} opacity={0.22} />
    <rect x={3} y={10.5} width={14} height={3} rx={1.5} />
    <path d="M13 6.5 19 12l-6 5.5V6.5Z" />
    <circle cx={19} cy={12} r={1.2} fill={accent} />
  </svg>
);

// ChevronRight — compact chevron, spark = guidance
export const ChevronRightIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M9 5 17 12 9 19V5Z" opacity={0.22} />
    <path d="M9 5 17 12 9 19V5Z" />
    <circle cx={14} cy={12} r={1} fill={accent} />
  </svg>
);

// ChevronUp
export const ChevronUpIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M5 15 12 7l7 8H5Z" opacity={0.22} />
    <path d="M5 15 12 7l7 8H5Z" />
    <circle cx={12} cy={10} r={1} fill={accent} />
  </svg>
);

// ChevronDown
export const ChevronDownIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M5 9 12 17l7-8H5Z" opacity={0.22} />
    <path d="M5 9 12 17l7-8H5Z" />
    <circle cx={12} cy={14} r={1} fill={accent} />
  </svg>
);

// Menu — three lines, spark = active line
export const MenuIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <rect x={4} y={6} width={16} height={2.5} rx={1.25} opacity={0.22} />
    <rect x={4} y={6} width={16} height={2.5} rx={1.25} />
    <rect x={4} y={11} width={16} height={2.5} rx={1.25} />
    <rect x={4} y={16} width={10} height={2.5} rx={1.25} opacity={0.55} />
    <circle cx={18} cy={17.2} r={1.3} fill={accent} />
  </svg>
);

// Close — X badge, spark = release point
export const CloseIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <circle cx={12} cy={12} r={9} opacity={0.22} />
    <circle cx={12} cy={12} r={9} />
    <path d="m8.5 8.5 7 7M15.5 8.5l-7 7" stroke="#fff" strokeWidth={2} strokeLinecap="round" />
    <circle cx={12} cy={12} r={1} fill={accent} />
  </svg>
);

/* ─────────────── Greeting Icons ─────────────── */
// Morning — sunrise over horizon, spark = sun glow
export const MorningIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M3 18h18c0-4-4-7-9-7s-9 3-9 7Z" opacity={0.22} />
    <path d="M3 18h18c0-4-4-7-9-7s-9 3-9 7Z" />
    <circle cx={12} cy={9} r={3.2} opacity={0.55} />
    <rect x={3} y={18.5} width={18} height={2} rx={1} />
    <path d="M12 3v2M5.5 7l1.2 1.2M18.5 7l-1.2 1.2" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" opacity={0.5} />
    <circle cx={12} cy={9} r={1.4} fill={accent} />
  </svg>
);

// Afternoon — full sun with rays, spark = solar core
export const AfternoonIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <circle cx={12} cy={12} r={5.5} opacity={0.22} />
    <circle cx={12} cy={12} r={5.5} />
    <path d="M12 2.5v2.5M12 19v2.5M2.5 12h2.5M19 12h2.5M5 5l1.8 1.8M17.2 17.2 19 19M5 19l1.8-1.8M17.2 6.8 19 5" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" opacity={0.6} />
    <circle cx={12} cy={12} r={1.8} fill={accent} />
  </svg>
);

// Evening — crescent moon, spark = evening star
export const EveningIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M20 13.5c-1.5 1-3.2 1.5-5 1.5-4.4 0-8-3.6-8-8 0-1.8.5-3.5 1.5-5-3.5 1.5-6 5-6 9 0 5.5 4.5 10 10 10 4 0 7.5-2.5 9-6-1 .3-2 .5-3 .5-.5 0-1 0-1.5-.1Z" opacity={0.22} />
    <path d="M20 13.5c-1.5 1-3.2 1.5-5 1.5-4.4 0-8-3.6-8-8 0-1.8.5-3.5 1.5-5-3.5 1.5-6 5-6 9 0 5.5 4.5 10 10 10 4 0 7.5-2.5 9-6-1 .3-2 .5-3 .5-.5 0-1 0-1.5-.1Z" />
    <circle cx={17} cy={5.5} r={1.5} fill={accent} />
  </svg>
);

/* ─────────────── Marketplace / Settings aliases ─────────────── */
// Re-export with alternative names used across the codebase
export const MarketplaceIcon = CampusExchangeIcon;
export const DocumentIcon = DocumentsIcon;
export const MessageIcon = MessagesIcon;
export const SettingsIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <circle cx={12} cy={12} r={3.5} opacity={0.22} />
    <circle cx={12} cy={12} r={3.5} />
    <circle cx={12} cy={12} r={1.5} fill="#fff" opacity={0.9} />
    <path d="M12 1.5v3.5M12 19v3.5M1.5 12h3.5M19 12h3.5M4.5 4.5l2.5 2.5M17 17l2.5 2.5M4.5 19.5l2.5-2.5M17 7l2.5-2.5" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" opacity={0.55} />
    <circle cx={12} cy={12} r={0.8} fill={accent} />
  </svg>
);

// Brand mark alias for sidebar logo
export const BrandMarkIcon = LogoMark;

// Meal — fork and spoon, spark = steam
export const MealIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <rect x={5.5} y={3} width={1.8} height={9} rx={0.9} opacity={0.55} />
    <path d="M5.5 3c-2 0-3 2-3 4s1 3.5 3 3.5" opacity={0.22} />
    <path d="M5.5 3c-2 0-3 2-3 4s1 3.5 3 3.5" stroke="currentColor" strokeWidth={1.6} fill="none" />
    <rect x={5.5} y={10} width={1.8} height={11} rx={0.9} />
    <path d="M16 3c-2 0-3.5 2.5-3.5 5.5 0 2.5 1.5 4 3.5 4V21" opacity={0.22} />
    <path d="M16 3c-2 0-3.5 2.5-3.5 5.5 0 2.5 1.5 4 3.5 4V21" stroke="currentColor" strokeWidth={1.6} fill="none" />
    <rect x={15.1} y={3} width={1.8} height={18} rx={0.9} opacity={0.55} />
    <circle cx={19} cy={6} r={1.3} fill={accent} />
  </svg>
);

// Uniform — shirt/vest, spark = badge
export const UniformIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M8 3.5 12 5.5l4-2 4.5 2.5V20H3.5V6L8 3.5Z" opacity={0.22} />
    <path d="M8 3.5 12 5.5l4-2 4.5 2.5V20H3.5V6L8 3.5Z" />
    <path d="M12 5.5V20" opacity={0.3} />
    <rect x={10.8} y={8.5} width={2.4} height={3} rx={0.5} fill="#fff" opacity={0.9} />
    <circle cx={12} cy={10} r={1.1} fill={accent} />
  </svg>
);

/* ─────────────── Landing-page utility icons ─────────────── */
// Play — triangle in circle, spark = focal dot
export const PlayIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <circle cx={12} cy={12} r={9} opacity={0.22} />
    <circle cx={12} cy={12} r={9} />
    <path d="M10 8 16 12l-6 4V8Z" fill="#fff" opacity={0.95} />
    <circle cx={12} cy={12} r={1.1} fill={accent} />
  </svg>
);

// Chart — bars + trending line, spark = peak bar
export const ChartIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <rect x={3.5} y={13} width={3.5} height={7.5} rx={0.8} opacity={0.22} />
    <rect x={3.5} y={13} width={3.5} height={7.5} rx={0.8} />
    <rect x={8.5} y={9} width={3.5} height={11.5} rx={0.8} opacity={0.55} />
    <rect x={13.5} y={5} width={3.5} height={15.5} rx={0.8} />
    <path d="M4 11 9 7l5 3 6-6" stroke="currentColor" strokeWidth={1.6} fill="none" strokeLinecap="round" strokeLinejoin="round" opacity={0.5} />
    <circle cx={15.2} cy={7} r={1.3} fill={accent} />
  </svg>
);

// Zap — lightning bolt, spark = charge point
export const ZapIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M13 2 4 13h6l-1 9 9-11h-6l1-9Z" opacity={0.22} />
    <path d="M13 2 4 13h6l-1 9 9-11h-6l1-9Z" />
    <circle cx={11} cy={11} r={1.3} fill={accent} />
  </svg>
);

// CreditCard — card with stripe, spark = chip
export const CreditCardIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <rect x={2.5} y={5} width={19} height={14} rx={2.4} opacity={0.22} />
    <rect x={2.5} y={5} width={19} height={14} rx={2.4} />
    <rect x={2.5} y={8.5} width={19} height={2.5} opacity={0.55} />
    <rect x={5} y={13} width={4.5} height={2.8} rx={0.6} fill="#fff" opacity={0.9} />
    <circle cx={15.5} cy={14.4} r={1.3} fill={accent} />
  </svg>
);

// Store — shopfront awning, spark = open sign
export const StoreIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M4 9h16l-1.5-4.5h-13L4 9Z" opacity={0.22} />
    <path d="M4 9h16l-1.5-4.5h-13L4 9Z" />
    <rect x={5} y={9} width={14} height={11} rx={1.4} opacity={0.55} />
    <rect x={9.5} y={13} width={5} height={7} rx={0.8} fill="#fff" opacity={0.9} />
    <circle cx={17.5} cy={6.5} r={1.3} fill={accent} />
  </svg>
);

// Target — concentric rings, spark = bullseye
export const TargetIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <circle cx={12} cy={12} r={9} opacity={0.22} />
    <circle cx={12} cy={12} r={9} />
    <circle cx={12} cy={12} r={6} opacity={0.3} />
    <circle cx={12} cy={12} r={3} opacity={0.55} />
    <circle cx={12} cy={12} r={1.5} fill={accent} />
  </svg>
);

// Award — medal ribbon, spark = center badge
export const AwardIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <circle cx={12} cy={9} r={6} opacity={0.22} />
    <circle cx={12} cy={9} r={6} />
    <circle cx={12} cy={9} r={3} fill="#fff" opacity={0.9} />
    <path d="M8.5 14 7 21l5-2.5L17 21l-1.5-7" opacity={0.55} />
    <circle cx={12} cy={9} r={1.4} fill={accent} />
  </svg>
);

// Scale — balance scales, spark = level point
export const ScaleIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <rect x={11} y={4} width={2} height={16} rx={1} opacity={0.22} />
    <rect x={11} y={4} width={2} height={16} rx={1} />
    <rect x={7} y={19} width={10} height={2} rx={1} opacity={0.55} />
    <path d="M4 9h16M12 5v4" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" />
    <path d="M4 9 2.5 14h3L4 9ZM20 9l-1.5 5h3L20 9Z" opacity={0.55} />
    <circle cx={12} cy={5} r={1.3} fill={accent} />
  </svg>
);

// MessageSquare — chat bubble, spark = typing dot
export const MessageSquareIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M4 4h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9l-4 4V5a1 1 0 0 1-1-1Z" opacity={0.22} />
    <path d="M4 4h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9l-4 4V5a1 1 0 0 1-1-1Z" />
    <circle cx={9} cy={10} r={1} fill="#fff" opacity={0.9} />
    <circle cx={12} cy={10} r={1} fill="#fff" opacity={0.9} />
    <circle cx={15} cy={10} r={1} fill={accent} />
  </svg>
);

// TrendingUp — arrow up-right with line, spark = peak
export const TrendingUpIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M4 16 9 11l4 2 7-7.5" opacity={0.22} />
    <path d="M4 16 9 11l4 2 7-7.5" stroke="currentColor" strokeWidth={2.2} fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15 7.5h5v5" stroke="currentColor" strokeWidth={2.2} fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx={13} cy={13} r={1.6} fill="#fff" opacity={0.9} />
    <circle cx={13} cy={13} r={1} fill={accent} />
  </svg>
);

// ArrowUpRight — diagonal arrow, spark = destination
export const ArrowUpRightIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M7 17 17 7M17 7H9M17 7v8" opacity={0.22} />
    <path d="M7 17 17 7M17 7H9M17 7v8" stroke="currentColor" strokeWidth={2.2} fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx={17} cy={7} r={1.2} fill={accent} />
  </svg>
);

// Heart — alias for HealthIcon for compatibility
export const HeartIcon = HealthIcon;

// TeachersIcon already exported above; alias BookOpen as Book for landing
export const BookIcon2 = BookIcon;

/* ─────────────── Navbar / auth icons ─────────────── */
// User — person silhouette in disc, spark = profile dot
export const UserIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <circle cx={12} cy={12} r={9} opacity={0.22} />
    <circle cx={12} cy={12} r={9} />
    <circle cx={12} cy={9} r={3.2} fill="#fff" opacity={0.9} />
    <path d="M6 20c0-3.5 2.5-6 6-6s6 2.5 6 6" fill="#fff" opacity={0.9} />
    <circle cx={16} cy={8} r={1.3} fill={accent} />
  </svg>
);

// LogOut — door with exit arrow, spark = departure point
export const LogOutIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <path d="M4 4h10v16H4z" opacity={0.22} />
    <path d="M4 4h10v16H4z" />
    <rect x={6} y={6} width={6} height={12} rx={1} fill="#fff" opacity={0.85} />
    <path d="M14 12h6M17 9l3 3-3 3" stroke="currentColor" strokeWidth={2} fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx={20} cy={12} r={1.2} fill={accent} />
  </svg>
);

// LayoutDashboard — grid panels, spark = active panel
export const LayoutDashboardIcon = ({ accent = SPARK, ...p }: TriIconProps) => (
  <svg {...svgProps(p)}>
    <rect x={3} y={3} width={8} height={8} rx={1.6} opacity={0.22} />
    <rect x={3} y={3} width={8} height={8} rx={1.6} />
    <rect x={13} y={3} width={8} height={5} rx={1.6} opacity={0.55} />
    <rect x={13} y={10} width={8} height={11} rx={1.6} />
    <rect x={3} y={13} width={8} height={8} rx={1.6} opacity={0.55} />
    <rect x={14.5} y={12} width={5} height={7.5} rx={1} fill="#fff" opacity={0.9} />
    <circle cx={17} cy={15.8} r={1.2} fill={accent} />
  </svg>
);

/* ─────────────── Legacy stroke-based aliases ─────────────── */
export type IconProps = { className?: string; style?: React.CSSProperties };

